import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from "react";
import { initializeApp } from "firebase/app";
import {
    getAuth,
    signInWithCustomToken,
    onAuthStateChanged,
    signOut,
    User,
    initializeAuth,
    browserLocalPersistence,
    indexedDBLocalPersistence
} from "firebase/auth";
import { doc, getFirestore, setDoc } from "firebase/firestore";
import { useDispatch, useSelector } from "react-redux";
import { apiConfigs } from "../constants/apiConfigs";
import { AppDispatch, RootState } from "../redux-stores/store";
import { isFresh } from "../utils/helpers";
import { fetchFirebaseBackendTokenThunk } from "../redux-stores/slices/authSlice";
import { getAnalytics, logEvent, setUserId } from "firebase/analytics";
import { APP_ENV } from "../constants/constants";

const FIREBASE_TOKEN_STALE_TIME = apiConfigs.CREATE_FIREBASE_TOKEN.staleTime;

const DISABLE_LOGS = true;
const platform: string = 'web';

// Initialize Firebase
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET
};

const app = initializeApp(firebaseConfig, {});

// Initialize auth with explicit persistence for iOS
const auth = platform === 'ios'
    ? initializeAuth(app, {
        persistence: [browserLocalPersistence] // Force localStorage on iOS
    })
    : getAuth(app);

const db = getFirestore(app, "chats");

const fa = getAnalytics(app);

// Enhanced logging for iOS debugging
const logDebug = (message: string, data?: any) => {
    if(DISABLE_LOGS) return;
    const timestamp = new Date().toISOString();
    console.log(`[Firebase ${platform}] ${timestamp} - ${message}`, data || '');
};

type AnalyticsParams = Record<string, any>;

export const analyticsLog = (
    event: string,
    params?: AnalyticsParams
) => {
    if (!fa) {
        if (!DISABLE_LOGS) {
            console.warn("[Analytics] Skipped (not initialized):", event);
        }
        return;
    }

    if(APP_ENV !== 'production') return;

    try {
        const updatedParams = {platform, ...(params ?? {})}
        logEvent(fa, event, updatedParams);
        logDebug("Analytics event logged", { event, updatedParams });
    } catch (error) {
        logDebug("Analytics event failed", { event, error });
    }
};


// Context types
interface FirebaseContextProps {
    firebaseUser: User | null;
    loading: boolean;
    auth: typeof auth;
    db: typeof db;
    fa: typeof fa;
    appUser: {
        name: string,
        profile_photo_url: string,
        uuid: string,
        access_type: string,
    } | null;
}

const FirebaseContext = createContext<FirebaseContextProps>({
    firebaseUser: null,
    loading: true,
    auth,
    db,
    appUser: null,
    fa
});

export const useFirebase = () => useContext(FirebaseContext);

// Provider
export const FirebaseProvider = ({ children }: { children: ReactNode }) => {
    const dispatch: AppDispatch = useDispatch();
    const firebaseCustomToken = useSelector((state: RootState) => state.auth.firebaseToken);
    const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const appUser = currentUser ? {
        name: currentUser?.name,
        profile_photo_url: currentUser?.profile_photo_url,
        uuid: currentUser?.uuid,
        access_type: currentUser?.access_type,
    } : null;
    const authenticated = useRef(false);
    const signInAttempts = useRef(0);

    // Debug environment on mount
    useEffect(() => {
        logDebug('=== Firebase Environment Debug ===');
        logDebug('Platform', platform);
        logDebug('Origin', window.location.origin);
        logDebug('Href', window.location.href);
        logDebug('IndexedDB available', 'indexedDB' in window);
        logDebug('LocalStorage available', typeof localStorage !== 'undefined');
        logDebug('SessionStorage available', typeof sessionStorage !== 'undefined');

        // Test storage access
        try {
            localStorage.setItem('test', 'test');
            localStorage.removeItem('test');
            logDebug('LocalStorage writable', true);
        } catch (e) {
            logDebug('LocalStorage writable', false);
            logDebug('LocalStorage error', e);
        }

        // Log auth instance
        logDebug('Auth instance', {
            app: auth.app.name,
            currentUser: auth.currentUser?.uid || 'none'
        });

        // On iOS, check if we need to disable app verification
        if (platform === 'ios') {
            logDebug('iOS detected - using localStorage persistence');
        }
    }, []);

    // Track Firebase auth state
    useEffect(() => {
        logDebug('Setting up auth state listener');

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            logDebug('Auth state changed', {
                uid: user?.uid || 'null',
                email: user?.email || 'null'
            });
            setFirebaseUser(user);
            setLoading(false);
        }, (error) => {
            logDebug('Auth state error', error);
            setLoading(false);
        });

        return () => {
            logDebug('Cleaning up auth state listener');
            unsubscribe();
        };
    }, []);

    // Set User ID to Firebase Analytics

    useEffect(() => {
        if(currentUser && fa){
            setUserId(fa, currentUser.uuid);
        }
    }, [currentUser, fa])

    // Sign in with custom token
    useEffect(() => {
        const tokenExpired = !isFresh(firebaseCustomToken?.generatedAt ?? null, FIREBASE_TOKEN_STALE_TIME);

        logDebug('Sign-in effect triggered', {
            hasToken: !!firebaseCustomToken,
            tokenExpired,
            authenticated: authenticated.current,
            hasFirebaseUser: !!firebaseUser,
            attempts: signInAttempts.current
        });

        if (firebaseCustomToken && !tokenExpired && !authenticated.current && !firebaseUser) {
            authenticated.current = true;
            signInAttempts.current += 1;

            logDebug('Starting signInWithCustomToken', {
                attempt: signInAttempts.current,
                tokenLength: firebaseCustomToken.token.length
            });

            // Create timeout promise to detect hanging
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => {
                    logDebug('Sign-in timeout after 15 seconds');
                    reject(new Error('Firebase signInWithCustomToken timeout after 15 seconds'));
                }, 15000)
            );

            Promise.race([
                signInWithCustomToken(auth, firebaseCustomToken.token),
                timeoutPromise
            ])
                .then((userCredential) => {
                    logDebug('Sign-in successful', {
                        uid: userCredential.user.uid,
                        email: userCredential.user.email
                    });
                    authenticated.current = false;
                    signInAttempts.current = 0;
                })
                .catch((error) => {
                    logDebug('Sign-in failed', {
                        code: error.code,
                        message: error.message,
                        name: error.name,
                        stack: error.stack
                    });
                    authenticated.current = false;

                    // If timeout, try to diagnose
                    if (error.message.includes('timeout')) {
                        logDebug('DIAGNOSIS: Sign-in is hanging. Possible causes:');
                        logDebug('1. Network requests being blocked by iOS');
                        logDebug('2. CORS issues with Firebase endpoints');
                        logDebug('3. Storage (IndexedDB/LocalStorage) access denied');
                        logDebug('4. WKWebView security restrictions');

                        // Check network connectivity
                        fetch('https://www.google.com', { mode: 'no-cors' })
                            .then(() => logDebug('Network connectivity: OK'))
                            .catch(() => logDebug('Network connectivity: FAILED'));
                    }

                    console.error('Firebase sign-in error:', error);
                });
        } else if (tokenExpired && firebaseUser) {
            logDebug('Token expired, signing out');
            signOut(auth).then(() => {
                authenticated.current = false;
                logDebug('Sign-out successful');
            }).catch((error) => {
                logDebug('Sign-out failed', error);
            });
        }
    }, [firebaseCustomToken, firebaseUser]);

    // Fetch backend token
    useEffect(() => {
        if (currentUser) {
            logDebug('Fetching Firebase backend token for user', currentUser.uuid);
            dispatch(fetchFirebaseBackendTokenThunk());
        }
    }, [dispatch, currentUser]);

    return (
        <FirebaseContext.Provider value={{ firebaseUser, loading, auth, db, appUser, fa }}>
            {children}
        </FirebaseContext.Provider>
    );
};